import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, MessageCircle, AlertCircle } from 'lucide-react';

export const SupportPage: React.FC = () => {
  return (
    <DocsLayout
      title='Support'
      description='Get help when you need it'
      breadcrumbs={[{ label: 'Documentation', path: '/docs' }, { label: 'Support' }]}
    >
      <div className='max-w-[1000px]'>
        <section className='mb-20'>
          <h1 className='text-3xl font-bold mb-6'>Support</h1>
          <div className='not-prose mb-6'>
            <Card className='border-primary/50 bg-primary/5'>
              <CardHeader>
                <CardTitle className='text-lg font-semibold'>We're Here to Help</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Our support team is committed to ensuring you get the most out of Vehix. Whether
                  you have questions, need technical assistance, or want to report an issue, we're
                  here to help.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>How to Get Support</h2>
          <div className='not-prose grid gap-4 md:grid-cols-2'>
            <Card>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <Mail className='h-8 w-8 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Email Support</CardTitle>
                </div>
              </CardHeader>
              <CardContent className='space-y-2 text-sm leading-6'>
                <div>
                  <strong>General Support:</strong>
                  <div className='text-primary'>contact@kebi.ai</div>
                </div>
                <div>
                  <strong>Response Time:</strong>
                  <div className='text-muted-foreground'>During business hours</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <MessageCircle className='h-8 w-8 text-primary' />
                  <CardTitle className='text-lg font-semibold'>In-App Chat</CardTitle>
                </div>
              </CardHeader>
              <CardContent className='space-y-2 text-sm leading-6'>
                <div>
                  <strong>Quick Questions:</strong>
                  <div className='text-muted-foreground'>
                    Use the chat widget in the bottom right
                  </div>
                </div>
                <div>
                  <strong>Response Time:</strong>
                  <div className='text-muted-foreground'>During business hours</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6' id='security-issues'>
            Report Security Issues
          </h2>
          <div className='not-prose'>
            <Card className='border-red-500/50 bg-red-500/5'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <AlertCircle className='h-8 w-8 text-red-600' />
                  <CardTitle className='text-lg font-semibold text-red-700 dark:text-red-400'>
                    Security Concerns
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='mb-3 text-sm leading-6'>
                  If you discover a security vulnerability or have concerns about data security,
                  please report it immediately:
                </p>
                <div className='text-sm leading-6'>
                  <strong>Security Email:</strong>
                  <div className='text-primary'>security@kebi.ai</div>
                </div>
                <div className='mt-3 text-sm leading-6 text-muted-foreground'>
                  We take security seriously and will respond to all security reports within 24
                  hours.
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Before Contacting Support</h2>
          <p className='text-sm leading-6 mb-4'>
            To help us assist you faster, please have the following information ready:
          </p>
          <ul className='list-disc pl-6 space-y-2 text-sm leading-6'>
            <li>Your organization name and user email</li>
            <li>Description of the issue or question</li>
            <li>Steps to reproduce (if reporting a bug)</li>
            <li>Browser and operating system information</li>
            <li>Screenshots (if applicable)</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Frequently Asked Questions</h2>

          <div className='space-y-6'>
            <div>
              <h3 className='text-lg font-semibold mb-2'>How do I reset my password?</h3>
              <p className='text-sm leading-6'>
                If you've forgotten your password, go to <strong>portal.vehix.ai</strong> and click
                the "Forgot my password" link on the sign-in screen. You'll receive an email with
                instructions to reset your password. You can also ask your service manager or
                administrator for help if needed.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-2'>Why can't I see certain features?</h3>
              <p className='text-sm leading-6'>
                Feature access is based on your assigned role. Contact your system administrator if
                you believe you should have access to a feature that's currently unavailable to you.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-2'>How do I request a new feature?</h3>
              <p className='text-sm leading-6'>
                We welcome feature requests! Email your ideas to <strong>contact@kebi.ai</strong>{' '}
                with the subject line "Feature Request". Include details about how the feature would
                help your workflow.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-2'>How do I learn to use Vehix?</h3>
              <p className='text-sm leading-6'>
                Your service manager will show you how to use Vehix for your daily tasks. You can
                also explore these documentation pages to learn about specific features. If you need
                additional help, email <strong>contact@kebi.ai</strong>.
              </p>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>System Status</h2>
          <p className='text-sm leading-6 mb-4'>
            Check the current status of Vehix services and view scheduled maintenance at our status
            page:
          </p>
          <p className='text-sm leading-6'>
            <strong>Status Page:</strong>{' '}
            <a
              href='https://status.kebi.ai'
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline'
            >
              status.kebi.ai
            </a>
          </p>
        </section>
      </div>
    </DocsLayout>
  );
};
